import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DEFAULT_ORGANIZATION_CHART } from './constants/organization-chart';
import {
  IOrganizationNode,
  IProgressOrganizationChart,
} from './models/organization-chart.types';

@Component({
  selector: 'app-organization-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './organization-chart.component.html',
  styleUrl: './organization-chart.component.scss',
})
export class OrganizationChartComponent implements OnInit, OnChanges {
  @Input() organizationChart?: IOrganizationNode;
  @Input() showCheckBox = false;
  @Input() showPhaseIndicator = true;
  @Input() maxNumberOfChecked: string | undefined = '';
  @Input() progress: IProgressOrganizationChart[] | null = []; // Used to show progress in the chart

  @Output() nodeSelectionChange = new EventEmitter<IOrganizationNode[]>();

  data!: IOrganizationNode;

  pictureUrlMap: { [prid: string]: string } = {};

  ngOnInit(): void {
    // Initialize data with default organization chart
    this.data = this.getDefaultData();

    // Update nodes with progress data if available
    if (this.progress && this.data) {
      this.updateNodesWithProgress();
    }

    // Load picture URLs for all nodes
    if (this.data) {
      this.setPictureUrlsForTree(this.data);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only reinitialize if organizationChart input changes
    if (changes['organizationChart']) {
      this.data = this.organizationChart || this.getDefaultData();
      // Load picture URLs when organization chart changes
      if (this.data) {
        this.setPictureUrlsForTree(this.data);
      }
    }

    // Check if maxNumberOfChecked changed to "Whole organization"
    // Only trigger if it's not the first change (initial value) and the new value is "Whole organization"
    if (
      changes['maxNumberOfChecked'] &&
      !changes['maxNumberOfChecked'].firstChange &&
      changes['maxNumberOfChecked'].currentValue === 'Whole organization'
    ) {
      this.selectAllNodes();
    }

    // Update nodes with progress data when progress changes
    if (changes['progress'] && this.progress && this.data) {
      this.updateNodesWithProgress();
    }
  }

  getDefaultData() {
    return DEFAULT_ORGANIZATION_CHART;
  }

  // Method to select all nodes when "Whole organization" is selected
  selectAllNodes(): void {
    if (this.data) {
      this.setAllNodesSelected(this.data, true);
      const selectedNodes = this.getSelectedNodes(this.data);
      this.nodeSelectionChange.emit(selectedNodes);
    }
  }

  // Helper method to recursively set all nodes as selected/unselected
  setAllNodesSelected(node: IOrganizationNode, selected: boolean): void {
    node.selected = selected;

    if (node.children) {
      for (const child of node.children) {
        this.setAllNodesSelected(child, selected);
      }
    }
  }

  // Helper method to check if a node should be disabled
  isNodeDisabled(node: IOrganizationNode): boolean {
    if (!this.showCheckBox) return false;

    // If "Whole organization" is selected, disable all checkboxes
    if (this.maxNumberOfChecked === 'Whole organization') {
      return true;
    }

    const maxAllowed = this.maxNumberOfChecked
      ? parseInt(this.maxNumberOfChecked, 10)
      : 0;
    if (maxAllowed <= 0) return false;

    // If node is already selected, it shouldn't be disabled
    if (node.selected) return false;

    // If we've reached the limit, disable unselected nodes
    const currentlySelected = this.getSelectedNodes(this.data);
    return currentlySelected.length >= maxAllowed;
  }

  onNodeCheckboxChange(node: IOrganizationNode, event: Event): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    // If maxNumberOfChecked is null, undefined, or empty, don't allow any changes
    if (!this.maxNumberOfChecked || this.maxNumberOfChecked.trim() === '') {
      target.checked = node.selected!; // Revert the checkbox state
      return;
    }

    // If "Whole organization" is selected, don't allow changes
    if (this.maxNumberOfChecked === 'Whole organization') {
      return;
    }

    // Parse maxNumberOfChecked to number for numeric limits
    const maxAllowed = this.maxNumberOfChecked
      ? parseInt(this.maxNumberOfChecked, 10)
      : 0;

    // If trying to check a node and there's a numeric limit
    if (isChecked && maxAllowed > 0) {
      // Get currently selected nodes
      const currentlySelected = this.getSelectedNodes(this.data);

      // Check if adding this node would exceed the limit
      if (currentlySelected.length >= maxAllowed) {
        // Prevent the selection
        target.checked = false;
        alert(`You can only select up to ${maxAllowed} members.`);
        return;
      }
    }

    // Update node selection
    node.selected = isChecked;

    // Get updated selected nodes
    const selectedNodes = this.getSelectedNodes(this.data);

    // Emit the change event
    this.nodeSelectionChange.emit(selectedNodes);
  }

  // Helper method to get all selected nodes
  private getSelectedNodes(
    node: IOrganizationNode | undefined
  ): IOrganizationNode[] {
    if (!node) return [];

    const selectedNodes: IOrganizationNode[] = [];

    if (node.selected) {
      selectedNodes.push(node);
    }

    if (node.children) {
      for (const child of node.children) {
        selectedNodes.push(...this.getSelectedNodes(child));
      }
    }

    return selectedNodes;
  }

  // Method to update nodes with progress data
  updateNodesWithProgress(): void {
    if (!this.progress || !this.data) return;

    this.updateNodeProgress(this.data);
  }

  // Helper method to recursively update nodes with progress data
  updateNodeProgress(node: IOrganizationNode): void {
    // Find matching progress for current node

    if (!this.progress) return;

    const progressData = this.progress.find(
      (progress_user) => progress_user.sk_user === node.sk_user
    );

    if (progressData) {
      node.level = progressData.level;
      node.score = progressData.score;
    }

    // Recursively update children
    if (node.children) {
      for (const child of node.children) {
        this.updateNodeProgress(child);
      }
    }
  }

  isPhaseActive(nodeLevel: string, phaseToCheck: string): boolean {
    // Return false if nodeLevel is undefined or null
    if (!nodeLevel || !phaseToCheck) {
      return false;
    }

    const phaseOrder = ['Awareness', 'Interest', 'Experimentation', 'Adoption'];
    const currentIndex = phaseOrder.indexOf(nodeLevel);
    const checkIndex = phaseOrder.indexOf(phaseToCheck);

    return currentIndex >= checkIndex && currentIndex !== -1;
  }

  async getPictureURL(prid: string): Promise<string> {
    try {
      const res = await fetch(
        `https://adhoc.rd.astrazeneca.net/api/thumbs/${prid}?appid=rdpo`
      );
      console.log(res);
      if (!res.ok) throw new Error('Image not found');
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error(error);
      // Return a fallback image if fetch fails
      return 'assets/default-profile.png';
    }
  }

  async setPictureUrl(prid: string) {
    if (!this.pictureUrlMap[prid]) {
      this.pictureUrlMap[prid] = await this.getPictureURL(prid);
    }
  }

  // Method to recursively set picture URLs for all nodes in the tree
  async setPictureUrlsForTree(node: IOrganizationNode) {
    if (node.prid) {
      await this.setPictureUrl(node.prid);
    }
    if (node.children) {
      for (const child of node.children) {
        await this.setPictureUrlsForTree(child);
      }
    }
  }
}
